import { type FileObject, TransactionDocument, TransactionDocumentList } from '@kalos/kalos-rpc';
import { type Query, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TransactionDocumentClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type TransactionDocumentFilter = EntityFilter<TransactionDocument>;
export const useTransactionDocumentsBatchQuery = <TSelectData = TransactionDocument[],>({
  enabled = true,
  filter = {},
  select,
}: {
  enabled?: boolean;
  select?: (data: TransactionDocumentList) => TSelectData;
  filter?: TransactionDocumentFilter;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.transactionDocument.root,
      queryKeys.transactionDocument.list,
      queryKeys.transactionDocument.getFilterHash(filter),
    ],
    queryFn: async () => {
      return await TransactionDocumentClientService.BatchGet(TransactionDocument.create(filter));
    },
    enabled,
    select,
  });
};

export const useDeleteTransactionDocumentByIDMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg: { fileId: number; transactionFileId: number }) => {
      return await TransactionDocumentClientService.DeleteTransactionDocument(arg);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [queryKeys.transactionDocument.root] });
    },
  });
};

const getPredicateForTransactionDocumentListById = (id: number) => {
  return (query: Query) => {
    if (
      !(
        query.queryKey.at(0) === queryKeys.transactionDocument.root &&
        query.queryKey.at(1) === queryKeys.transactionDocument.list
      )
    ) {
      return false;
    }

    const filter = query.queryKey.at(2);
    if (!filter || typeof filter !== 'string') {
      return false;
    }

    const parsedFilter = JSON.parse(filter) as unknown;
    if (
      typeof parsedFilter === 'object' &&
      parsedFilter &&
      'transactionId' in parsedFilter &&
      typeof parsedFilter.transactionId === 'number'
    ) {
      return parsedFilter.transactionId === id;
    }

    return false;
  };
};

export const useCreateTransactionDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg: TransactionDocument) => {
      return await TransactionDocumentClientService.Create(arg);
    },
    onSuccess(result) {
      queryClient.setQueriesData<TransactionDocumentList>(
        {
          predicate: getPredicateForTransactionDocumentListById(result.transactionId),
        },
        (cache) => {
          if (cache) {
            return TransactionDocumentList.create({
              results: [...cache.results, result],
              totalCount: cache.totalCount + 1,
            });
          }
        },
      );
      queryClient.invalidateQueries({
        queryKey: [queryKeys.transactionDocument.root, queryKeys.transaction.list],
      });
    },
  });
};

export const useUploadTransactionDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      transactionId,
      fileData,
      fileName,
    }: {
      transactionId: number;
      fileName: string;
      fileData: Uint8Array;
    }) => await TransactionDocumentClientService.upload(transactionId, fileName, fileData),
    onSuccess(data) {
      if (data) {
        queryClient.setQueriesData<TransactionDocumentList>(
          {
            predicate: getPredicateForTransactionDocumentListById(data.transactionId),
          },
          // A new object, so queries reading the list (and its count) see the change.
          (oldData) =>
            TransactionDocumentList.create({
              results: [...(oldData?.results ?? []), data],
              totalCount: (oldData?.totalCount ?? 0) + 1,
            }),
        );
      }
    },
  });
};

export const useTransactionDocumentDataByIdQuery = <TSelectData = FileObject,>({
  enabled = true,
  fileName,
  select,
}: {
  enabled?: boolean;
  fileName: string;
  select?: (data?: FileObject) => TSelectData;
}) => {
  return useQuery({
    queryKey: [queryKeys.transactionDocument.root, queryKeys.transactionDocument.data, fileName],
    queryFn: async () => {
      return await TransactionDocumentClientService.download(0, fileName);
    },
    select,
    enabled,
  });
};
